"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Field = exports.showField = void 0;
const core_1 = require("../core");
const utils_1 = require("../message/utils");
const interface_1 = require("../interface");
const private_1 = require("../utils/private");
const core_2 = require("@apitable/core");
const store_1 = require("../store");
/**
 * @hidden
 * Enterprise Wecom compatibility judgment,
 * three-party applications do not support the display of member fields.
 * @param type
 * @returns
 */
const showField = (type) => {
    if (!window['_isSocialWecom']) {
        return true;
    }
    if (![interface_1.FieldType.CreatedBy, interface_1.FieldType.LastModifiedBy, interface_1.FieldType.Member].includes(type)) {
        return true;
    }
    return navigator.userAgent.toLowerCase().indexOf('wxwork') === -1;
};
exports.showField = showField;
const getNoAcceptableFieldString = (type, fieldName) => {
    const stringMap = {
        [interface_1.FieldType.Member]: (0, core_1.t)(core_1.Strings.wecom_widget_member_field_name, { fieldName }),
        [interface_1.FieldType.CreatedBy]: (0, core_1.t)(core_1.Strings.wecom_widget_created_by_field_name, { fieldName }),
        [interface_1.FieldType.LastModifiedBy]: (0, core_1.t)(core_1.Strings.wecom_widget_last_edited_by_field_name, { fieldName }),
    };
    return stringMap[type] || 'NoAcceptable';
};
// Field Description Length
const MAX_DESC = 200;
/**
 * Number datasheet column operations and information.
 *
 * To manipulate the number datasheet columns,
 * you can use {@link useField} (single column information),
 * {@link useFields} (multiple column information).
 */
class Field {
    /**
     * @hidden
     */
    constructor(datasheetId, wCtx, fieldData) {
        this.datasheetId = datasheetId;
        this.wCtx = wCtx;
        this.fieldData = fieldData;
        const state = wCtx.widgetStore.getState();
        this.fieldEntity = core_1.Field.bindContext(fieldData, state);
    }
    /**
     * Checks whether the current user has permission to perform the given field update.
     */
    checkPermissionUpdateField() {
        if (!this.fieldEntity.propertyEditable()) {
            return (0, private_1.errMsg)(`No write access to ${this.fieldData.name}(${this.id}) column`);
        }
        return { acceptable: true };
    }
    /**
     * The ID for this model.
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myField.id);
     * // => 'fld1234567'
     * ```
     */
    get id() {
        return (0, exports.showField)(this.type) ? this.fieldData.id : (0, core_2.getNewId)(core_2.IDPrefix.Field);
    }
    /**
     * The name of the field. Can be watched.
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myField.name);
     * // => 'Name'
     * ```
     */
    get name() {
        return (0, exports.showField)(this.type) ? this.fieldData.name : getNoAcceptableFieldString(this.type, this.fieldData.name);
    }
    /**
     * The type of the field. Can be watched. {@link FieldType}
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myField.type);
     * // => 'SingleLineText'
     * ```
     */
    get type() {
        return (0, core_1.getFieldTypeString)(this.fieldData.type);
    }
    /**
     * The description of the field, if it has one. Can be watched.
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myField.description);
     * // => 'This is my field'
     * ```
     */
    get description() {
        return this.fieldData.desc || null;
    }
    /**
     *
     * The configuration property of the field.
     * The structure of the field's property depend on the field's type.
     * null if the field has no property. Can be watched.
     * Refer to {@link FieldType}.
     *
     * @return {@link FieldType}
     *
     * #### Example
     * ```js
     * import { FieldType } from '@apitable/widget-sdk';
     *
     * if (myField.type === FieldType.Currency) {
     *     console.log(myField.options.symbol);
     *     // => '￥'
     * }
     * ```
     */
    get property() {
        return this.fieldEntity.openFieldProperty;
    }
    /**
     * true if this field is computed, false otherwise.
     * A field is "computed" if it's value is not set by user input (e.g. autoNumber, magic lookup,  magic formula, etc.). Can be watched
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(mySingleLineTextField.isComputed);
     * // => false
     * console.log(myAutoNumberField.isComputed);
     * // => true
     * ```
     */
    get isComputed() {
        return this.fieldEntity.isComputed;
    }
    /**
     * TODO: Leave for compatibility, subsequent deletion
     * @hidden
     * true if this field is its parent table's primary field, false otherwise.
     * Should never change because the primary field of a datasheet cannot change.
     * @returns
     */
    get isPrimaryField() {
        return this.isPrimary;
    }
    /**
     * true if this field is its parent table's primary field, false otherwise.
     * Should never change because the primary field of a datasheet cannot change.
     * @returns
     */
    get isPrimary() {
        const state = this.wCtx.widgetStore.getState();
        const snapshot = (0, store_1.getSnapshot)(state, this.datasheetId);
        return Boolean((snapshot === null || snapshot === void 0 ? void 0 : snapshot.meta.views[0].columns[0].fieldId) === this.fieldData.id);
    }
    /**
     * Is the magic form required.
     */
    get required() {
        return this.fieldData.required || null;
    }
    /**
     * Get the current view feature properties, such as whether the field is hidden in a view
     *
     * @param viewId the view ID
     * @return
     *
     * #### Example
     * ``` js
     * const propertyInView = field.getPropertyInView('viwxxxxx');
     * console.log(propertyInView?.hidden)
     * ```
     */
    getPropertyInView(viewId) {
        const state = this.wCtx.widgetStore.getState();
        const snapshot = (0, store_1.getSnapshot)(state, this.datasheetId);
        const view = snapshot && core_1.Selectors.getViewById(snapshot, viewId);
        const viewField = view === null || view === void 0 ? void 0 : view.columns.find(column => column.fieldId === this.id);
        if (!viewField) {
            return null;
        }
        return {
            hidden: viewField.hidden,
        };
    }
    /**
     * Updates the description for this field.
     *
     * Throws an error if the user does not have permission to update the field, or if an invalid description is provided.
     *
     * @param description new description for the field
     * @returns
     *
     * #### Example
     * ```js
     *  field.updateDescription('this is a new description')
     * ```
     */
    updateDescription(description) {
        const desc = description || undefined;
        if (description && description.length > MAX_DESC) {
            throw new Error('Description exceeds the maximum length limit of 200');
        }
        return new Promise((resolve) => __awaiter(this, void 0, void 0, function* () {
            const result = yield (0, utils_1.cmdExecute)({
                cmd: core_1.CollaCommandName.SetFieldAttr,
                datasheetId: this.datasheetId,
                fieldId: this.fieldData.id,
                data: Object.assign(Object.assign({}, this.fieldData), { desc })
            }, this.wCtx.id);
            if (result.result === core_1.ExecuteResult.Fail) {
                throw new Error(result.reason);
            }
            if (result.result === core_1.ExecuteResult.None) {
                throw new Error('update description method has been ignored');
            }
            resolve();
        }));
    }
    /**
     *
     * Beta API, future changes are possible.
     *
     * Updates the property for this field,
     * tips: that the update property configuration must be overwritten in full.
     *
     * Throws an error if the user does not have permission to update the field,
     * if invalid property are provided, if this field has no writable property, or if updates to this field type is not supported.
     *
     * Refer to {@link FieldType} for supported field types, the write format for property, and other specifics for certain field types.
     *
     * @param property new property for the field.
     * @param options optional options to affect the behavior of the update.
     * @returns
     *
     * #### Example
     * ```js
     * function addOptionToSelectField(selectField, nameForNewOption) {
     *     const updatedOptions = {
     *         options: [
     *             ...selectField.options,
     *             {name: nameForNewOption},
     *         ]
     *     };
     *
     *     if (selectField.hasPermissionToUpdateOptions(updatedOptions)) {
     *         selectField.updateProperty(updatedOptions);
     *     }
     * }
     * ```
     */
    updateProperty(property, options) {
        const { error } = this.fieldEntity.validateUpdateOpenProperty(property, options);
        if (error) {
            throw new Error(JSON.stringify(error));
        }
        const updateProperty = this.fieldEntity.updateOpenFieldPropertyTransformProperty(property);
        let deleteBrotherField;
        // Magic link special fields, need to determine whether to delete the associated fields of the associated table
        if (this.type === interface_1.FieldType.MagicLink) {
            const { conversion } = property;
            deleteBrotherField = conversion === core_1.Conversion.Delete;
        }
        return new Promise((resolve) => __awaiter(this, void 0, void 0, function* () {
            const result = yield (0, utils_1.cmdExecute)({
                cmd: core_1.CollaCommandName.SetFieldAttr,
                datasheetId: this.datasheetId,
                fieldId: this.fieldData.id,
                deleteBrotherField,
                data: Object.assign(Object.assign({}, this.fieldData), { property: updateProperty })
            }, this.wCtx.id);
            if (result.result === core_1.ExecuteResult.Fail) {
                throw new Error(result.reason);
            }
            if (result.result === core_1.ExecuteResult.None) {
                throw new Error('update property method has been ignored');
            }
            resolve();
        }));
    }
    /**
     *
     * Checks whether the current user has permission to perform the given description update.
     *
     * @param description new description for the field, Length limit 200.
     * @returns
     *
     * #### Example
     * ``` js
     * const canUpdateFieldDescription = field.hasPermissionForUpdateDescription();
     * if (!canUpdateFieldDescription) {
     *   alert('not allowed!');
     * }
     * ```
     */
    hasPermissionForUpdateDescription(description) {
        if (description && description.length > 200) {
            return false;
        }
        return this.checkPermissionUpdateField().acceptable;
    }
    /**
     *
     * Check whether the current user has permission to perform the given option update.
     *
     * Property about the update write format, refer to {@link FieldType}.
     *
     * @param property  new property for the field.
     * @returns
     *
     * #### Example
     * ``` js
     * const canUpdateFieldProperty = field.hasPermissionForUpdateProperty();
     * if (!canUpdateFieldProperty) {
     *   alert('not allowed!');
     * }
     * ```
     */
    hasPermissionForUpdateProperty(property) {
        return this.checkPermissionForUpdateProperty(property).acceptable;
    }
    /**
     * Check whether the current user has permission to perform the given option update.
     *
     * @param property new property for the field.
     * @returns
     *
     *
     * #### Description
     * Accepts partial input, in the same format as {@link updateProperty}.
     *
     * property about the update write format, refer to {@link FieldType}.
     *
     * Returns `{acceptable: true}` if the current user can update the specified property.
     *
     * Returns `{acceptable: false, message: string}` if no permission to operate, message may be used to display an error message to the user.
     *
     * #### Example
     * ```js
     * // Check whether the current user has permission to perform the given property update,
     * // when the update is accompanied by a write, it can also be verified at the same time.
     * const updatePropertyCheckResult = field.checkPermissionForUpdateProperty({
     *   defaultValue: '1',
     * });
     * if (!updatePropertyCheckResult.acceptable) {
     *   alert(updatePropertyCheckResult.message);
     * }
     *
     * // Check if user could potentially update a property.
     * // Use when you don't know the specific a property you want to update yet (for example,
     * // to show or hide UI controls that let you start update a property.)
     * const updatePropertyCheckResult =
     *   field.checkPermissionForUpdateProperty();
     * ```
     */
    checkPermissionForUpdateProperty(property) {
        const updateFieldPermissionResult = this.checkPermissionUpdateField();
        if (!updateFieldPermissionResult.acceptable) {
            return updateFieldPermissionResult;
        }
        if (!this.fieldEntity.propertyEditable()) {
            return (0, private_1.errMsg)(`No write access to ${this.fieldData.name}(${this.id}) column`);
        }
        if (property) {
            const { error } = this.fieldEntity.validateUpdateOpenProperty(property);
            if (error) {
                return (0, private_1.errMsg)(`
          ${this.fieldData.name}field, current write value  ${JSON.stringify(property)} format that does not match, please check:${error.message}
        `);
            }
        }
        return { acceptable: true };
    }
    /**
     * @hidden
     * For fields that are not lookup, entityType === type.
     */
    get entityType() {
        if (this.type === interface_1.FieldType.MagicLookUp) {
            const lookUpEntityField = this.fieldEntity.getLookUpEntityField();
            if (!lookUpEntityField)
                return interface_1.FieldType.NotSupport;
            return (0, core_1.getFieldTypeString)(lookUpEntityField.type);
        }
        return this.type;
    }
    /**
     * @hidden
     * Base type of field values.
     */
    get basicValueType() {
        return this.fieldEntity.basicValueType;
    }
    /**
     * @hidden
     * Some fields have formatting, get the formatting type.
     */
    get formatType() {
        var _a;
        switch (this.type) {
            case interface_1.FieldType.DateTime:
            case interface_1.FieldType.CreatedTime:
            case interface_1.FieldType.LastModifiedTime:
                const { dateFormat, timeFormat, includeTime } = this.fieldData.property;
                return {
                    type: 'datetime',
                    formatting: { dateFormat, timeFormat, includeTime },
                };
            case interface_1.FieldType.Number: {
                const { precision, commaStyle } = this.fieldData.property;
                return {
                    type: 'number',
                    formatting: { precision, commaStyle },
                };
            }
            case interface_1.FieldType.Currency:
                const { symbol, precision } = this.fieldData.property;
                return {
                    type: 'currency',
                    formatting: { symbol, precision },
                };
            case interface_1.FieldType.Percent:
                return {
                    type: 'percent',
                    formatting: { precision: this.fieldData.property.precision },
                };
            case interface_1.FieldType.Formula:
            case interface_1.FieldType.MagicLookUp:
                if (!((_a = this.fieldData.property) === null || _a === void 0 ? void 0 : _a.formatting))
                    return null;
                switch (this.fieldEntity.valueType) {
                    case core_1.BasicValueType.Number:
                        const { symbol, precision, formatType } = this.fieldData.property.formatting;
                        switch (formatType) {
                            case core_1.FieldType.Number:
                                return {
                                    type: 'number',
                                    formatting: { precision },
                                };
                            case core_1.FieldType.Percent:
                                return {
                                    type: 'percent',
                                    formatting: { precision },
                                };
                            case core_1.FieldType.Currency:
                                return {
                                    type: 'currency',
                                    formatting: { symbol, precision },
                                };
                            default:
                                return null;
                        }
                    case core_1.BasicValueType.DateTime:
                        const { dateFormat, timeFormat, includeTime } = this.fieldData.property.formatting;
                        return {
                            type: 'datetime',
                            formatting: { dateFormat, timeFormat, includeTime },
                        };
                    default:
                        return null;
                }
        }
        return null;
    }
    /**
     * @hidden
     * Attempts to convert data of string type to when compatible with the data type of the current field.
     *
     * If it is not compatible, it returns null.
     *
     * @param string The string to parse.
     * #### Example
     * ```js
     * const inputString = '42';
     * const cellValue = myNumberField.convertStringToCellValue(inputString);
     * console.log(cellValue === 42);
     * // => true
     * ```
     */
    convertStringToCellValue(string) {
        const stdValue = {
            sourceType: core_1.FieldType.Text,
            data: [{ text: string }],
        };
        return this.fieldEntity.stdValueToCellValue(stdValue);
    }
    /**
     * @hidden
     */
    convertCellValueToString(cv) {
        let _cv = cv;
        if (this.basicValueType === core_1.BasicValueType.Array) {
            _cv = _cv == null ? null : [cv].flat();
        }
        return this.fieldEntity.cellValueToString(_cv, { datasheetId: this.datasheetId });
    }
    /**
     * @hidden
     */
    getFieldResultByStatType(statType, records) {
        const recordIds = records.map(record => record.id);
        const state = this.wCtx.widgetStore.getState();
        const snapshot = core_1.Selectors.getSnapshot(state, this.datasheetId);
        return (0, core_1.getFieldResultByStatType)(statType, recordIds, this.fieldData, snapshot, state);
    }
    /**
     * @hidden
     */
    statType2text(type) {
        return this.fieldEntity.statType2text(type);
    }
    /**
     * @hidden
     */
    get statTypeList() {
        return this.fieldEntity.statTypeList;
    }
}
exports.Field = Field;
